import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { toast } from "sonner";
import { queries } from "@/lib/queries";
import { recordChannelVisit } from "@/lib/recent-channels";
import { readNotificationNavigationIntent } from "@/lib/navigation-intent";
import { isChannelTrusted } from "@/lib/channel-navigation-trust";

interface UserLike {
  id: string;
}

interface UseChannelNavigationArgs {
  user: UserLike | null | undefined;
}

/**
 * Owns the `selectedChannel` cursor + the navigation-intent target
 * pointers (targetMessageId / targetThreadId) + the channels-by-user
 * query that backs the multiplex's workspace-scope eviction.
 *
 * `openChannelWithContext` itself is intentionally NOT owned here — it
 * touches setters across this hook AND `useTopLevelView` AND the scope
 * cursor in `useWorkspaceScope`. Composing it inside `AppInner` keeps
 * its body byte-identical with the pre-extraction implementation
 * (App.tsx lines 599–631 in the legacy file).
 */
export function useChannelNavigation({ user }: UseChannelNavigationArgs) {
  const [selectedChannel, setSelectedChannel] = useState<string | null>(() => {
    const fromNotification = readNotificationNavigationIntent(
      window.location.href,
    ).channelId;
    if (fromNotification) return fromNotification;
    return localStorage.getItem("ano-channel");
  });

  const [targetMessageId, setTargetMessageId] = useState<string | null>(
    () => readNotificationNavigationIntent(window.location.href).messageId,
  );
  const [targetThreadId, setTargetThreadId] = useState<string | null>(
    () => readNotificationNavigationIntent(window.location.href).threadId,
  );

  // Track channel visits to drive the Sidebar's LRU-based preload order.
  // Covers every entry path (sidebar click, inbox click, notification deep-link,
  // URL restore) because they all funnel through setSelectedChannel.
  useEffect(() => {
    if (user && selectedChannel) {
      recordChannelVisit(user.id, selectedChannel);
    }
  }, [user, selectedChannel]);

  // Persist selections across reloads
  useEffect(() => {
    if (selectedChannel) {
      localStorage.setItem("ano-channel", selectedChannel);
    } else {
      localStorage.removeItem("ano-channel");
    }
  }, [selectedChannel]);

  // channelId → workspace_id lookup for the multiplex render. Each
  // kept-alive MessageArea instance gets its OWN channel's workspace so
  // hidden instances don't inherit the active workspace and end up
  // subscribing to the wrong workspace_members / workspace-channels data.
  // DMs (workspace_id IS NULL in the schema) fall back to the active
  // workspace — mentions inside a DM are resolved against the viewer's
  // current workspace context, same as before this fix.
  const [allUserChannelsRaw, allUserChannelsStatus] = useQuery(
    user ? queries.channels.byUser() : null,
  );
  // Primitive — guards downstream effect from re-running on every IVM tick
  // emission. Status object itself is a fresh ref per emission.
  const isByUserComplete = allUserChannelsStatus?.type === "complete";
  const channelWorkspaceMap = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const c of allUserChannelsRaw ?? []) {
      map.set(c.id, c.workspace_id ?? null);
    }
    return map;
  }, [allUserChannelsRaw]);

  // Deleted-channel guard. If `selectedChannel` is set but the channel
  // disappears from `allUserChannelsRaw` (deleted by an admin in
  // another tab, soft-removed from membership), revert the selection
  // to the sidebar default. Reuses the existing `byUser` subscription
  // — no extra Zero query.
  //
  // Only toast when a channel was previously confirmed in `byUser` and
  // then disappears — not when the user just navigated to a new DM that
  // hasn't landed in the `byUser` subscription yet (sidebar member click,
  // getOrCreateDirectDM, etc.).
  const confirmedChannelsRef = useRef(new Set<string>());
  const selectionChangedAtRef = useRef<number | null>(null);
  useEffect(() => {
    selectionChangedAtRef.current = Date.now();
  }, [selectedChannel]);

  useEffect(() => {
    if (!selectedChannel || !allUserChannelsRaw) return;
    // Wait for byUser to settle before evicting. While the query is
    // re-syncing (workspace switch, Zero reconnect, post-mutation flip),
    // `allUserChannelsRaw` may be transiently empty or stale — a known
    // channel can look "missing" for a tick or two even though it's not.
    // Eviction here used to fire the "no longer available" toast on the
    // first click of a member with an existing-but-just-unhidden DM,
    // because the server's unhide hadn't propagated through Zero yet.
    if (!isByUserComplete) return;

    const exists = allUserChannelsRaw.some((c) => c.id === selectedChannel);
    if (exists) {
      confirmedChannelsRef.current.add(selectedChannel);
      return;
    }

    // Channel was just confirmed by an explicit open flow (useOpenDM via
    // zero.run one-shot). The subscription frame hasn't arrived yet —
    // give it the full trust TTL before evicting.
    if (isChannelTrusted(selectedChannel)) return;

    const wasConfirmed = confirmedChannelsRef.current.has(selectedChannel);
    const msSinceSelection =
      selectionChangedAtRef.current == null
        ? 0
        : Date.now() - selectionChangedAtRef.current;
    const graceMs = wasConfirmed ? 150 : msSinceSelection < 3000 ? 2500 : 150;

    const timeout = window.setTimeout(() => {
      const stillMissing = !allUserChannelsRaw.some(
        (c) => c.id === selectedChannel,
      );
      if (!stillMissing) return;
      if (wasConfirmed) {
        toast.error("That channel is no longer available.");
        confirmedChannelsRef.current.delete(selectedChannel);
      }
      // Reacting to external state (channel disappeared from server data)
      // is the canonical reason to setState in an effect — there's no
      // event to attach this to.

      setSelectedChannel((current) =>
        current === selectedChannel ? null : current,
      );
    }, graceMs);

    return () => window.clearTimeout(timeout);
  }, [selectedChannel, allUserChannelsRaw, isByUserComplete]);

  const handleTargetMessageHandled = useCallback(() => {
    setTargetMessageId(null);
    setTargetThreadId(null);
  }, []);

  return {
    selectedChannel,
    setSelectedChannel,
    targetMessageId,
    setTargetMessageId,
    targetThreadId,
    setTargetThreadId,
    handleTargetMessageHandled,
    allUserChannelsRaw,
    channelWorkspaceMap,
  };
}
